// src/components/Header.tsx
"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import Container from "./Container";
import { getAllMediaTypesAction } from "@/app/actions/collection";
import { useTranslations, useLocale } from "next-intl";

interface NavLink {
  href: string;
  label: string;
  key: string;
}

export default function Header() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const t = useTranslations("nav");

  // Start as [] (not null) so the header shell renders on the server and
  // on the first client paint. Dynamic items simply pop in when ready.
  const [navLinks, setNavLinks] = useState<NavLink[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const mediaTypes = await getAllMediaTypesAction();
        if (cancelled) return;

        setNavLinks(
          mediaTypes.map((mediaType) => ({
            // Prefix with the active locale so next-intl's middleware
            // doesn't have to 307 on every click.
            href: `/${locale}/timeline?media_type=${mediaType.key}`,
            label: mediaType.title,
            key: mediaType.key,
          })),
        );
        setFailed(false);
      } catch (error) {
        console.error("Failed to load media types:", error);
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [locale]);

  const currentMediaType = searchParams.get("media_type");
  const onTimeline = pathname?.endsWith("/timeline") ?? false;

  // Home is active only when we're on /timeline with no media-type filter.
  const homeLink: NavLink = {
    href: `/${locale}/timeline`,
    label: t("home"),
    key: "home",
  };

  const allLinks = [homeLink, ...navLinks];

  return (
    <header
      style={{
        borderBottom: "1px solid var(--card-border)",
        position: "sticky",
        top: 0,
        background: "var(--background)",
        zIndex: 50,
      }}
    >
      <Container>
        <nav
          className="scroll-strip"
          style={{
            height: 56,
            alignItems: "center",
            padding: "0",
            gap: "0",
          }}
          aria-label="Main navigation"
        >
          {allLinks.map(({ href, label, key }) => {
            const isActive =
              key === "home"
                ? onTimeline && !currentMediaType
                : currentMediaType === key;

            return (
              <Link
                key={key}
                href={href}
                style={{
                  padding: "0.5rem 0.85rem",
                  borderRadius: "0.375rem",
                  fontSize: "0.9rem",
                  fontWeight: isActive ? "600" : "400",
                  whiteSpace: "nowrap",
                  background: isActive
                    ? "var(--category-active-bg)"
                    : "transparent",
                  color: isActive ? "var(--category-active-color)" : "inherit",
                  minHeight: 36,
                  display: "inline-flex",
                  alignItems: "center",
                }}
                aria-current={isActive ? "page" : undefined}
              >
                {label}
              </Link>
            );
          })}

          {failed && (
            <span
              aria-live="polite"
              style={{
                fontSize: "0.75rem",
                opacity: 0.5,
                padding: "0 0.5rem",
              }}
            >
              ·
            </span>
          )}
        </nav>
      </Container>
    </header>
  );
}
